import { List, Record } from 'immutable';
import _ from 'lodash';
import { v1 as uuidv1 } from 'uuid';

import config from '../config';
import PlusBus from './PlusBus';
import Search from './Search';
import SeasonSearch from './SeasonSearch';
import OverallCheapestSelection from './OverallCheapestSelection';
import DaySaveSearch from './DaySaveSearch';
import RoversRangersSearch from './RoversRangersSearch';
import TripFares from './TripFares';
import Photocard from './Photocard';
import DisplayFilters from './DisplayFilters';
import SeatingPreferences from './SeatingPreferences';
import Service from './Service';
import { resolve } from '../helpers/LinkMapper';
import {
    TripTypes,
    TicketTypes,
    JourneyDirections,
    TimeTypes,
    FareCategories,
    FareClasses,
    JourneyPlannerLayout,
    TransPennineStaffRoverNlcCodes,
    OrderTripTypes,
} from '../constants/AppConstants';
import Passengers from './Passengers';
import Fares from './Fares';
import { annotateFareLegsWithTripId } from '../helpers/Fares';

const defaults = {
    id: null,
    fares: new TripFares(),
    fareCategory: null,
    fareCategoryDetail: null,
    fareTicketTypeCode: null,
    fareRouteCode: null,
    fareDestination: null,
    fareOrigin: null,
    selectingCheapestFare: true,
    fareClass: null,
    outwardJourney: null,
    returnJourney: null,
    reservationPreferences: new SeatingPreferences(),
    photocard: new Photocard(),
    passengers: new Passengers(),
    orderTripId: null,
    search: new Search(),
    plusBusAvailability: null,
    cheapest: new OverallCheapestSelection(),
    cheapestFastest: new OverallCheapestSelection(),
    cheapestFirstClass: new OverallCheapestSelection(),
    filters: new DisplayFilters(),
    hasServiceProvider: false,
    listStep: null,
    raw: true,
    bikes: 0,
    isSeatsRequested: false,
    splitSalt: null,
    outwardSplitReferenceId: null,
    returnSplitReferenceId: null,
    originalOutwardJourney: null,
    originalReturnJourney: null,
};


// A service's own timing when there's a live selection, otherwise the order's original,
// as-booked journey for whatever's not being amended this session (Service and OrderJourney
// expose the same departure/arrival concept under different method names). A journey merged
// from legs that ended up empty can return null from either, so both are required before this
// counts as usable timing.
function _timingFor (service, fallbackJourney = null) {
    if (service) {
        return { departure: service.getDepartureDateTime(), arrival: service.getArrivalDateTime() };
    }
    if (fallbackJourney) {
        const departure = fallbackJourney.getDepartureTime();
        const arrival = fallbackJourney.getArrivalTime();
        return departure && arrival ? { departure, arrival } : null;
    }
    return null;
}

// `inclusive` matches servicesOverlap()'s pre-existing "departs at or before arrival still
// counts as overlapping" rule for the trip's own already-selected services; the amend candidate
// checks keep the strict "departs before arrival" rule they had before this helper was shared,
// since widening their boundary would flag an exact back-to-back connection that was previously
// allowed.
function _timingOverlaps (inwardTiming, outwardTiming, { inclusive = false } = {}) {
    if (!inwardTiming || !outwardTiming) {
        return false;
    }
    return inclusive
        ? !inwardTiming.departure.isAfter(outwardTiming.arrival)
        : inwardTiming.departure.isBefore(outwardTiming.arrival);
}

// Shared by both directions' amend candidate checks - only the roles of the fixed vs. candidate
// service differ between them.
function _candidateOverlaps (inwardService, outwardService, originalReturnJourney, originalOutwardJourney) {
    return _timingOverlaps(
        _timingFor(inwardService, originalReturnJourney),
        _timingFor(outwardService, originalOutwardJourney)
    );
}

//HACK: make this better
function _searchClass (search, searchClassName) {
    if (searchClassName) {
        switch (searchClassName) {
            case 'DaySaveSearch': return DaySaveSearch;
            case 'SeasonSearch': return SeasonSearch;
            case 'RoversRangersSearch': return RoversRangersSearch;
            default: return Search;
        }
    }
    if (search.date && search.origin) {
        return DaySaveSearch;
    }
    if (search.date && search.ticketType) {
        return RoversRangersSearch;
    }
    if (search.dates) {
        return SeasonSearch;
    }
    return Search;
}

class Trip extends Record(defaults) { // eslint-disable-line new-cap

    constructor (values = {}) {
        if (!values.id) {
            values.id = uuidv1();
        }

        super(values);
    }


    static fromJSON (json = {}) {
        // Preprocess fares for split ticketing
        if (json.fares) {
            json.fares = annotateFareLegsWithTripId(json.fares);
        }

        const searchClass = _searchClass(json.search, json.searchClass);

        return new Trip({
            id: json.id,
            fares: resolve(json.fares).as(TripFares),
            fareCategory: json.fareCategory,
            fareClass: json.fareClass,
            outwardJourney: resolve(json.outwardJourney).as(Service),
            returnJourney: resolve(json.returnJourney).as(Service),
            reservationPreferences: resolve(json.reservationPreferences).as(SeatingPreferences),
            photocard: resolve(json.photocard).as(Photocard),
            passengers: resolve(json.passengers).as(Passengers),
            orderTripId: json.orderTripId,
            search: resolve(json.search).as(searchClass),
            plusBusAvailability: resolve(json.plusBusAvailability).as(PlusBus),
            cheapest: resolve(json.cheapest).as(OverallCheapestSelection),
            cheapestFastest: resolve(json.cheapestFastest).as(OverallCheapestSelection),
            cheapestFirstClass: resolve(json.cheapestFirstClass).as(OverallCheapestSelection),
            filters: resolve(json.filters).as(DisplayFilters),
            hasServiceProvider: json.hasServiceProvider,
            listStep: json.listStep,
            raw: json.raw,
            bikes: json.bikes,
            isSeatsRequested: json.isSeatsRequested,
            splitSalt: json.splitSalt,
            outwardSplitReferenceId: json.outwardSplitReferenceId,
            returnSplitReferenceId: json.returnSplitReferenceId,
        });
    }

    getType () {

        if (this.search instanceof DaySaveSearch) {
            return TripTypes.DAYSAVE;
        }

        if (this.search instanceof RoversRangersSearch) {
            return TripTypes.ROVERSRANGERS;
        }

        const isSeason = this.search instanceof SeasonSearch;

        if (this.isSmartcard()) {
            if (isSeason) {
                return TripTypes.SMARTCARD_SEASON;
            }
            return TripTypes.SMARTCARD_POINT;
        }

        if (isSeason) {
            return TripTypes.SEASON;
        }
        return TripTypes.POINT;
    }

    needsJourney () {
        return this.getType() === TripTypes.POINT || this.getType() === TripTypes.SMARTCARD_POINT;
    }

    isPoint () {
        return this.search instanceof Search;
    }

    isSeason () {
        return this.search instanceof SeasonSearch;
    }

    isDaySave () {
        return this.search instanceof DaySaveSearch;
    }

    isRovers () {
        return this.search instanceof RoversRangersSearch;
    }

    isSingle () {
        return !this.isSeason() && this.getTicketType() === TicketTypes.SINGLE;
    }

    isReturn () {
        return !this.isSeason() && this.getTicketType() === TicketTypes.RETURN;
    }

    isOpenReturn () {
        return !this.isSeason() && this.getTicketType() === TicketTypes.OPEN_RETURN;
    }

    getTicketType () {
        return this.search.ticketType;
    }

    isSmartcard () {
        return !!this.search.smartcard;
    }

    isTransPennineStaffRover () {
        const originNlc = _.get(this.search, 'origin.nlc', null);
        const destinationNlc = _.get(this.search, 'destination.nlc', null);

        if (!originNlc || !destinationNlc) {
            return false;
        }

        return originNlc === TransPennineStaffRoverNlcCodes.ORIGIN && destinationNlc === TransPennineStaffRoverNlcCodes.DESTINATION;
    }

    getFareForDirection (direction) {
        if (direction === JourneyDirections.INWARD) {
            return this.fares.returnSingle;
        }
        return this.fares.return || this.fares.outwardSingle;
    }

    getSplitForDirection (direction) {
        if (direction === JourneyDirections.INWARD) {
            return this.fares.returnSplit;
        }
        return this.fares.outwardSplit;
    }

    getServiceForDirection (direction) {
        return direction === JourneyDirections.INWARD ? this.returnJourney : this.outwardJourney;
    }

    setFareForDirection (direction, fare) {
        const blankFares = new TripFares({
            plusBus: this.fares.plusBus
        });
        if (fare && fare.isReturn()) {
            return this.set('fares', blankFares.set('return', fare));
        }

        if (direction === JourneyDirections.INWARD) {
            return this
                .set('returnSplitReferenceId', null)
                .set(
                    'fares',
                    blankFares
                        .set('returnSingle', fare)
                        .set('outwardSingle', this.fares.get('outwardSingle'))
                        .set('outwardSplit', this.fares.get('outwardSplit'))
                );
        }

        return this
            .set('outwardSplitReferenceId', null)
            .set(
                'fares',
                blankFares
                    .set('outwardSingle', fare)
                    .set('returnSingle', this.fares.get('returnSingle'))
                    .set('returnSplit', this.fares.get('returnSplit'))
            );
    }

    setSplitForDirection (direction, split) {
        const blankFares = new TripFares({
            plusBus: this.fares.plusBus
        });

        if (direction === JourneyDirections.INWARD) {
            return this
                .set('returnSplitReferenceId', split.splitId)
                .set(
                    'fares',
                    blankFares
                        .set('returnSplit', split)
                        .set('outwardSingle', this.fares.get('outwardSingle'))
                        .set('outwardSplit', this.fares.get('outwardSplit'))
                );
        }
        return this
            .set('outwardSplitReferenceId', split.splitId)
            .set(
                'fares',
                blankFares
                    .set('outwardSplit', split)
                    .set('returnSingle', this.fares.get('returnSingle'))
                    .set('returnSplit', this.fares.get('returnSplit'))
            );
    }

    setFareOnlyFare (fare) {
        return this.set('fares', new TripFares({
            fareOnly: fare
        }));
    }

    resetFares () {
        return this.set('fares', new TripFares({
            plusBus: this.fares.plusBus
        }));
    }

    getOysterPrice () {
        if (this.isSingle()) {
            if (this.outwardJourney) {
                return this.outwardJourney.oysterPrice || null;
            }
        }
        if (this.isReturn()) {
            if (this.outwardJourney && this.returnJourney) {
                if (this.outwardJourney.oysterPrice && this.returnJourney.oysterPrice) {
                    return this.outwardJourney.oysterPrice + this.returnJourney.oysterPrice;
                }
            }
        }
        return null;
    }

    resetSelected (direction) {
        if (direction === JourneyDirections.INWARD && this.search.targetLayout === JourneyPlannerLayout.LIST) {
            return this.setIn(['fares', 'returnSingle'], null)
                .setIn(['fares', 'returnSplit'], null)
                .set('returnSplitReferenceId', null)
                .set('returnJourney', null);
        }

        return this.set('fares', new TripFares())
            .set('fareClass', null)
            .set('outwardSplitReferenceId', null)
            .set('returnSplitReferenceId', null)
            .set('returnJourney', null)
            .set('outwardJourney', null)
            .set('photocard', new Photocard());
    }

    setServiceForDirection (direction, service) {
        const journeyKey = direction === JourneyDirections.INWARD ? 'returnJourney' : 'outwardJourney';
        return this.set(journeyKey, service);
    }

    setSeatingPreferences (seatingPreferences) {
        return this.set('reservationPreferences', seatingPreferences);
    }

    setSeatingForDirection (direction, fare, service) {
        const shouldReserve = fare.isSeatCompulsory() || service.isSeatCompulsory() || service.isSeatAvailable();
        let newTrip = this.setIn(['reservationPreferences', direction], shouldReserve);

        if (!newTrip.reservationPreferences.shouldReserve()) {
            newTrip = newTrip.set('reservationPreferences', newTrip.reservationPreferences.resetPreferences());
        }
        return newTrip;
    }

    hasSeatingPreferences () {
        return this.reservationPreferences.hasPreferences();
    }

    getOutwardFare () {
        return this.getFareForDirection(JourneyDirections.OUTWARD);
    }

    hasOutwardFare () {
        return !!this.getOutwardFare();
    }

    getOutwardService () {
        return this.outwardJourney;
    }

    hasOutwardService () {
        return !!this.getOutwardService();
    }

    getInwardFare () {
        return this.getFareForDirection(JourneyDirections.INWARD);
    }

    hasInwardFare () {
        return !!this.getInwardFare();
    }

    getFareOnlyFare () {
        return this.fares.fareOnly;
    }

    hasFareOnlyFare () {
        return !!this.fares.fareOnly;
    }

    getReturnFare () {
        return this.fares.return;
    }

    hasReturnFare () {
        return !!this.fares.return;
    }

    getInwardService () {
        return this.returnJourney;
    }

    hasInwardService () {
        return !!this.getInwardService();
    }

    // Candidate checks deliberately don't gate on isReturn() like servicesOverlap() does: during
    // a single-leg amend, search.ticketType (and so isReturn()) reflects only the fare being
    // amended right now, not whether the underlying order has both directions.
    isInwardServiceBeforeOutwardArrival (service = this.getInwardService()) {
        return _candidateOverlaps(service, this.getOutwardService(), this.originalReturnJourney, this.originalOutwardJourney);
    }

    isOutwardServiceAfterInwardDeparture (service = this.getOutwardService()) {
        return _candidateOverlaps(this.getInwardService(), service, this.originalReturnJourney, this.originalOutwardJourney);
    }

    getOutwardAvailabilityFare () {
        const fare = this.getReturnFare() || this.getOutwardFare();
        const service = this.getOutwardService();
        if (fare && fare.canSelectSeatOnLeg) { return fare; }
        return service ? service.getCheapestForDirection(JourneyDirections.OUTWARD) : null;
    }

    getInwardAvailabilityFare () {
        const fare = this.getInwardFare();
        const service = this.getInwardService();
        if (fare && fare.canSelectSeatOnLeg) { return fare; }
        return service ? service.getCheapestForDirection(JourneyDirections.INWARD) : null;
    }

    getPlusBus () {
        return this.fares.plusBus;
    }

    hasPlusBus () {
        return Boolean(this.getPlusBus().count());
    }

    setPlusBus (plusBus) {
        return this.setIn(['fares', 'plusBus'], plusBus);
    }

    getServiceOrderForDirection (direction) {
        const { arriveDepart } = this.search.getTimebandForDirection(direction);
        return arriveDepart === TimeTypes.DEPART ? 'origin' : 'destination';
    }

    isSeatingAvailable () {
        const outwardService = this.getOutwardService();
        const inwardService = this.getInwardService();

        const outwardFare = outwardService ? (this.getReturnFare() || this.getOutwardFare()) : null;
        const inwardFare = inwardService
            ? (this.getReturnFare() || this.getInwardFare() || inwardService.getCheapestForDirection(JourneyDirections.INWARD))
            : null;

        return (!!outwardService && outwardService.isSeatAvailable(outwardFare))
            || (!!inwardService && inwardService.isSeatAvailable(inwardFare));
    }

    hasSeasonFare () {
        return !!this.getSeasonFare();
    }

    getSeasonFare () {
        return this.fares.season;
    }

    setSeasonFare (fare) {
        return this.setIn(['fares', 'season'], fare);
    }

    hasDaySaveFare () {
        return !!this.getDaySaveFare();
    }

    getDaySaveFare () {
        return this.fares.daySave;
    }

    setDaySaveFare (fare) {
        return this.setIn(['fares', 'daySave'], fare);
    }

    hasRoversRangersFare () {
        return !!this.getRoversRangersFare();
    }

    getRoversRangersFare () {
        return (this.getOutwardFare() && this.getOutwardFare().isRoversRangers()) ? this.getOutwardFare() : null;
    }

    setRoversRangersFare (fare) {
        return this.setIn(['fares', 'roversRangers'], fare);
    }

    getTotalPrice () {
        let total = this.getPlusBus().getTotalPrice();
        if (this.hasOutwardFare()) {
            total += this.getOutwardFare().getPrice();
        }
        if (this.hasInwardFare()) {
            total += this.getInwardFare().getPrice();
        }
        if (this.hasSeasonFare()) {
            total += this.getSeasonFare().getPrice();
        }
        if (this.hasFareOnlyFare()) {
            total += this.getFareOnlyFare().getPrice();
        }

        return total;
    }

    getTotalPriceWithoutPromo() {
        let total = this.getPlusBus().getTotalPrice();

        if (this.hasOutwardFare()) {
            total += this.getOutwardFare().getOriginalTotalPrice();
        }
        if (this.hasInwardFare()) {
            total += this.getInwardFare().getOriginalTotalPrice();
        }
        if (this.hasSeasonFare()) {
            total += this.getSeasonFare().getOriginalTotalPrice();
        }
        if (this.hasFareOnlyFare()) {
            total += this.getFareOnlyFare().getOriginalTotalPrice();
        }

        return total;
    }

    getTotalPriceWithoutDiscount () {
        let total = this.getPlusBus().getTotalPrice();

        const getRailcardDiscount = (fare) => fare.tickets.reduce((tot, ticket) => tot + (ticket.railcardDiscount || 0), 0);

        if (this.hasOutwardFare()) {
            total += this.getOutwardFare().getPrice() + getRailcardDiscount(this.getOutwardFare());
        }
        if (this.hasInwardFare()) {
            total += this.getInwardFare().getPrice() + getRailcardDiscount(this.getInwardFare());
        }
        if (this.hasSeasonFare()) {
            total += this.getSeasonFare().getPrice() + getRailcardDiscount(this.getSeasonFare());
        }
        if (this.hasFareOnlyFare()) {
            total += this.getFareOnlyFare().getPrice() + getRailcardDiscount(this.getFareOnlyFare());
        }

        return total;
    }

    isJPValid () {
        if (this.hasFareOnlyFare()) {
            return true;
        }

        const outward = this.getOutwardFare();

        if (!this.hasOutwardService() || !this.hasOutwardFare()) {
            return false;
        }

        if (this.isReturn() && !outward.isReturn() && !this.hasInwardFare()) {
            return false;
        }

        if (this.isReturn() && outward.isReturn() && !this.hasInwardService()) {
            return false;
        }

        return !this.servicesOverlap();
    }

    /**
     * When you attempt to evaluate a seasons trip make sure if it isSmartcard() you check that
     * smartcard is set and that smartcard.canLoadProduct()
     *
     * @returns Boolean
     * */
    isSeasonsValid () {
        if (this.hasFareOnlyFare() && this.getFareOnlyFare().isSeason()) {
            return (this.isSmartcard() || this.photocard.isComplete());
        }

        return this.search.isValid() && this.hasSeasonFare();
    }

    isDaySaveValid () {
        return this.search.isValid() && this.hasOutwardFare();
    }

    isRoversValid () {
        return this.search.isComplete() && this.hasOutwardFare() && this.passengers.isComplete();
    }

    isValid () {
        if (this.isSeason()) {
            return this.isSeasonsValid();
        } else if (this.isDaySave()) {
            return this.isDaySaveValid();
        } else if (this.isRovers()) {
            return this.isRoversValid();
        }
        return this.isJPValid();
    }

    servicesOverlap (inward = this.getInwardService(), outward = this.getOutwardService()) {
        return this.isReturn() && _timingOverlaps(_timingFor(inward), _timingFor(outward), { inclusive: true });
    }

    isCheapestFare (fare) {
        return this.cheapest.isCheapestFare(fare);
    }

    getCheapestFareCategory (cheapest = this.cheapest) {
        if ((this.isReturn() || this.search.isReturnLondonTravelcard()) && cheapest.hasReturnFare()) {
            return cheapest.getReturnFare().category;
        } else if (this.isReturn() && (cheapest.hasOutwardFare() || cheapest.hasInwardFare())) {
            return FareCategories.ADVANCE;
        } else if (cheapest.hasOutwardFare()) {
            return cheapest.getOutwardFare().category;
        }

        return FareCategories.ADVANCE;
    }

    getCheapestFareClass (cheapest = this.cheapest) {
        if (this.isReturn() && cheapest.hasReturnFare()) {
            return cheapest.getReturnFare().getClass();
        } else if (this.isReturn() && (cheapest.hasOutwardFare() || cheapest.hasInwardFare())) {
            return FareClasses.STANDARD;
        } else if (cheapest.hasOutwardFare()) {
            return cheapest.getOutwardFare().getClass();
        }

        return FareClasses.STANDARD;
    }

    getCheapestFareFromOverAll (fareCategory, fareClass, journeyDirection) {
        if (
            this.getCheapestFareCategory() !== fareCategory
            || this.getCheapestFareClass() !== fareClass
            || (fareCategory === FareCategories.ADVANCE && this.cheapest.hasReturnFare())
            || (fareCategory !== FareCategories.ADVANCE && this.isReturn() && !this.cheapest.hasReturnFare())
        ) {
            return null;
        }

        return this.cheapest.getFareForDirection(journeyDirection);
    }

    isPlusBusAvailable () {
        if (this.isSmartcard()){
            return config.get('enablePlusBus') && config.get('enableSmartPlusBus');
        }
        return config.get('enablePlusBus');
    }

    getOutwardJourneyTrainRid () {
        return this.outwardJourney.journey.legs
            && this.outwardJourney.journey.legs.first()
            && this.outwardJourney.journey.legs.first().serviceDetails.trainRid;
    }

    getCheapestFirstClassOpenReturnFare (services) {
        return services.getAllFares(this, true).filter(Fares.isFirstClass).sort((a, b) => a.getPrice() - b.getPrice()).first();
    }

    getAdditionalInfoPrioritized() {
        const stations = [this.search.destination, this.search.origin];
        const infos = stations
            .map(station => station && station.displayInfo && {
                additionalInfo: station.displayInfo.get('stationAdditionalDescription'),
                additionalInfoLink: station.displayInfo.get('stationAdditionalDescriptionLink'),
                stationType: station.stationType,
            })
            .filter(info => info && info.additionalInfo);

        const priority = ['airport', 'group', 'regular'];
        for (const type of priority) {
            const found = infos.find(info => info.stationType && info.stationType.toLowerCase() === type);
            if (found) { return found; }
        }

        return infos[0] || { additionalInfo: null, additionalInfoLink: null };
    }

    getJourneyFares () {
        const fares = this.fares;

        let journeyFares = new List();
        switch (this.getType()) {
            case OrderTripTypes.DAYSAVE:
                journeyFares = List.of(this.getDaySaveFare());
                break;
            case OrderTripTypes.ROVERSRANGERS:
                journeyFares = List.of(this.getRoversRangersFare());
                break;
            case OrderTripTypes.FARE_ONLY:
                journeyFares = List.of(this.getFareOnlyFare());
                break;
            case OrderTripTypes.RETURN:
                journeyFares = List.of(fares.return);
                break;
            case OrderTripTypes.RETURN_SINGLES:
                journeyFares = List.of(fares.outwardSingle, fares.returnSingle);
                break;
            case OrderTripTypes.RETURN_SINGLE:
                journeyFares = List.of(fares.returnSingle);
                break;
            case OrderTripTypes.SEASON:
                journeyFares = List.of(fares.season);
                break;
            default:
                journeyFares = List.of(fares.outwardSingle);
        }

        return journeyFares.filter((x) => !!x);
    }

    getSplitId() {
        return this.getFareSplitId() || this.outwardSplitReferenceId || this.returnSplitReferenceId || null;
    }

    isSplitTrip() {
        return !!this.getSplitId();
    }

    getFareSplitId() {
        const fares = this.getJourneyFares();
        const fare = fares?.find(f => f?.isPartOfSplit());
        return fare?.splitId;
    }
}

export default Trip;
